/**
 * 書類の作成履歴（証明書・届出書）
 * 船員アプリ・管理サイトで作成した書類の入力内容を履歴として一覧し、陸上から再出力できる。
 * 船側で作れないとき（権限・端末トラブル）に陸上が代行して再発行するための入口。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw, X } from 'lucide-react';
import * as api from '../services/api';

interface HistoryRow {
  id: string; form: string; form_title: string; format: string; source: string;
  created_by_name: string | null; created_at: string; subject: string;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
const subdomain = () => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; };
const authHeaders = () => ({ Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': subdomain(), 'X-App-Type': 'admin' });

/** 雇入系・社内様式は Excel、航行報告系は Word（作成画面と同じ出し分け） */
const EXCEL_FORMS = new Set(['koyoiri', 'henko', 'roster', 'shipsupply', 'travelexp', 'roumu', 'employment']);

function fmt(ts: string): string {
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? ts : d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function CertFormHistoryPanel({ form, onClose, onLoadIntoForm }: {
  form?: string; onClose: () => void;
  /** 履歴の入力内容を作成画面に読み込む（対応している画面だけ渡す） */
  onLoadIntoForm?: (form: string, data: Record<string, unknown>) => void;
}) {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [onlyThisForm, setOnlyThisForm] = useState(!!form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<{ data: HistoryRow[] }>('/cert-forms/history', onlyThisForm && form ? { form, limit: '200' } : { limit: '200' });
      setRows(r?.data ?? []);
    } catch { setRows([]); }
    setLoading(false);
  }, [form, onlyThisForm]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async (row: HistoryRow, fmtKey: 'pdf' | 'excel' | 'word') => {
    setBusy(`${row.id}:${fmtKey}`);
    try {
      const res = await fetch(`${API_BASE}/cert-forms/history/${row.id}/regenerate?format=${fmtKey}`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.error?.message ?? `HTTP ${res.status}`); }
      const blob = await res.blob();
      const ext = fmtKey === 'pdf' ? 'pdf' : fmtKey === 'word' ? 'docx' : 'xlsx';
      const url = URL.createObjectURL(blob);
      if (fmtKey === 'pdf') { window.open(url, '_blank'); }
      else { const a = document.createElement('a'); a.href = url; a.download = `${row.form_title}.${ext}`; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { alert(e?.message ?? '再出力に失敗しました'); }
    setBusy(null);
  };

  const loadInto = async (row: HistoryRow) => {
    if (!onLoadIntoForm) return;
    try {
      const r = await api.get<{ data: { form: string; data: Record<string, unknown> } }>(`/cert-forms/history/${row.id}`);
      onLoadIntoForm(r.data.form, r.data.data);
    } catch (e: any) { alert(e?.message ?? '読み込みに失敗しました'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 'min(760px, 96vw)', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-6px 0 24px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <History size={18} />
          <div style={{ fontWeight: 800, fontSize: 15 }}>書類の作成履歴</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>船員アプリ・管理サイトで作成した書類を、同じ内容で再出力できます</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {form && (
              <label style={{ fontSize: 12, display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={onlyThisForm} onChange={e => setOnlyThisForm(e.target.checked)} />この様式だけ
              </label>
            )}
            <button onClick={load} title="更新" style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 8px', cursor: 'pointer' }}><RefreshCw size={14} /></button>
            <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && <div style={{ padding: 24, color: '#94a3b8', fontSize: 13 }}>読み込み中…</div>}
          {!loading && rows.length === 0 && <div style={{ padding: 30, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>作成履歴はまだありません（これから作成した書類がここに残ります）</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {rows.map(r => {
                const excel = EXCEL_FORMS.has(r.form);
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#475569' }}>{fmt(r.created_at)}</td>
                    <td style={{ padding: '10px 8px' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{r.form_title}</div>
                      <div style={{ fontSize: 11.5, color: '#64748b' }}>
                        {r.subject || '—'}
                        <span style={{ marginLeft: 8 }}>{r.created_by_name ?? ''}{r.source === 'crew' ? '（船員アプリ）' : r.source === 'admin' ? '（管理サイト）' : ''}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button disabled={!!busy} onClick={() => regenerate(r, 'pdf')} style={btn('#0284c7')}>{busy === `${r.id}:pdf` ? '…' : 'PDF'}</button>
                      {excel
                        ? <button disabled={!!busy} onClick={() => regenerate(r, 'excel')} style={btn('#15803d')}>{busy === `${r.id}:excel` ? '…' : 'Excel'}</button>
                        : <button disabled={!!busy} onClick={() => regenerate(r, 'word')} style={btn('#6d28d9')}>{busy === `${r.id}:word` ? '…' : 'Word'}</button>}
                      {onLoadIntoForm && <button onClick={() => loadInto(r)} style={btn('#334155')}>作成画面へ読み込む</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function btn(bg: string): React.CSSProperties {
  return { marginLeft: 6, padding: '6px 10px', borderRadius: 8, border: 'none', background: bg, color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer' };
}
